import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface ExpansionHistoryPageProps {
}

const ExpansionHistoryPage = (props: ExpansionHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_post_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Expansion Age - Chase the Fox`,
        description: "The Expansion Age of Casarok is the time when nations began recontacting nations lost from the Great Cataclysm!",
        keywords: ["casarok", "casarok history", "expansion age", "fantasy timeline", "fantasy world history", "fantasy colonial time"],
        imgSrc: _map_src,
        url: `world/history/expansion-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Post-Cataclysm Map'
                         alt="A map of Casarok after the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Expansion Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(`chase/world/geography/casarok_post_cataclysm.png`)}
                             title='Casarok Post-Cataclysm Map'
                             alt="A map of Casarok after the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years 1300 - 1500 ACE.</em>
                    </p>
                    <p>
                        During the Expansion Age, ships starts utilizing recent technological improvements to cross the
                        violent oceans that had been created from
                        the <NavLink className="world-link" to='/world/history/cataclysmic-age'>Great Cataclysm</NavLink>. Since
                        these nations are no longer accessible by land, communication had not been established between
                        many of these nations in centuries. Thus, it acts like a post-Catalysmic version of
                        the <NavLink className="world-link" to='/world/history/crusade-age'>Crusade Age</NavLink> in a
                        way. All the nations began to rebuild trade networks by crossing the seas. Sharing culture,
                        ideas, and technology became critical to the strategy of many growing empires.
                    </p>
                    <p>
                        Many conflicts arose during this time period due to attempts bvy some nations to colonize the
                        lands of already existing nations. Colonization became a hotly debated issue in the nations
                        seeking to build imperial power. As such some nations refrained from doing so to keep the
                        support of their people. Others, however, were more concerned with power and legacy. Still,
                        it was difficult to establish colonies. While the imperial nations were armed with more
                        advanced weaponry, the encroached-upon nations had powerful new magic to rival them.
                    </p>
                    <p>
                        <em>How does Casarok's Expansion Age differ from the colonial era of Earth?</em><br/>
                        Not many nations were conquered or eradicated to establish new colonies. Many of the colonies
                        ended up being more like immigration with the host country setting aside land for the new
                        arrivals. Much of this did not happen without conflict, though, but they occurred on equal
                        footing. Healing through magic staved off diseases that killed many indigenous peoples during
                        Earth's colonial time too.
                    </p>
                    <h3 className="headline">Notable Events</h3>
                    <ul className="spaced">
                        <li>
                            <em>1307 ACE</em>: The nation of Ramos is formed, breaking away from Horpeta.
                        </li>
                        <li>
                            <em>1329 ACE</em>: The first ship from Centralia makes landfall on the coast of the
                            Garebolian continent.
                        </li>
                        <li>
                            <em>1344 ACE</em>: The first ship from Terasama arrives on Centralia, landing in Waleya.
                        </li>
                        <li>
                            <em>1353 ACE</em>: The nation of Ramos conquers part of Notahbe and claims its territory.
                        </li>
                        <li>
                            <em>1361 ACE</em>: First contact is made between countries on Centralia and the Snowscape as
                            Goretzka navigates the Frostladen Ocean for the first time to reach Vicar.
                        </li>
                        <li>
                            <em>1370 ACE</em>: The first skirmishes arise between immigrants and their host countries on
                            the continents of Centralia and Sequea.
                        </li>
                        <li>
                            <em>1379 ACE</em>: The Coreptuus clan
                            of <NavLink className="world-link" to="/world/bestiary/draconic/dracamma">dracammas</NavLink> centered
                            around <NavLink className="world-link" to="/world/geography">Mt. Coreptus</NavLink> rise up
                            and form their own nation of Colaspa, breaking away from Waleya.
                        </li>
                        <li>
                            <em>1388 ACE</em>: Advancements are made in steam and clockwork power, leading to more
                            powerful ships and some improved tech.
                        </li>
                        <li>
                            <em>1394 ACE</em>: The nation of Nyamea breaks off from Achiyu due to ethnic concerns and
                            sympathies with colonials.
                        </li>
                        <li>
                            <em>1402 ACE</em>: A fleet of Garebolian, Polonian, and Strepanese ships sail all the way to
                            unoccupied territory in the Frostlands.
                        </li>
                        <li>
                            <em>1423 ACE</em>: Muskets are invented, edging the power of firearms
                            over <NavLink className="world-link" to='/world/magic/items/bulletguard'>bulletguards</NavLink> once
                            again.
                        </li>
                        <li>
                            <em>1434 ACE</em>: The Frostfire Road is found by the Garebolians, leading to their first
                            contact with the Arlyndians and Zendelanders.
                        </li>
                        <li>
                            <em>1445 ACE</em>: Remote islands are new discoveries for the peoples of the main
                            continents, marking the first time in centuries all peoples of the world have had contact
                            with each other.
                        </li>
                        <li>
                            <em>1478 ACE</em>: Bulletguards capable of handling muskets and other current gunpowder
                            weapons become readily available to the public.
                        </li>
                        <li>
                            <em>1485 ACE</em>: The nation of Viraco secedes from Itzamnia during a civil war there.
                        </li>
                        <li>
                            <em>1492 ACE</em>: The nation of Donbey breaks from Jarlan and begins to invest in dark
                            magics.
                        </li>
                        <li>
                            <em>1496 ACE</em>: More big advancements are made in steam and clockwork power. The first
                            primitive zeppelins are built, capable of carrying some people over land.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default ExpansionHistoryPage;